/**
 * @fileoverview Chart Mode Sidebar Component
 * Collapsible sidebar with signal picker and controls.
 * 
 * @module charts/components/ChartModeSidebar
 */

import { el } from '../../shared/components/simpleRender.js';
import { ALL_SIGNAL_IDS, SIGNAL_IDS } from '../../api/capitulators.js';

// ============================================================================
// State
// ============================================================================

let sidebarRef = null;
let isCollapsed = false;
let activeSignals = [];
let addSignalCallback = null;
let removeSignalCallback = null;

// ============================================================================
// Constants
// ============================================================================

const CATEGORY_NAMES = {
  liquidity: 'Liquidity',
  credit: 'Credit',
  rates: 'Rates',
  funding: 'Funding',
  volatility: 'Volatility',
};

const CATEGORY_ICONS = {
  liquidity: '💧',
  credit: '💳',
  rates: '📈',
  funding: '🏦',
  volatility: '⚡',
};

const MAX_SIGNALS = 4;

// ============================================================================
// Signal Picker
// ============================================================================

function buildSignalPicker() {
  const picker = el('div', { className: 'signal-picker' }, []);
  
  // Build category groups
  Object.entries(SIGNAL_IDS).forEach(([categoryId, signalIds]) => {
    const categoryName = CATEGORY_NAMES[categoryId] || categoryId;
    const categoryIcon = CATEGORY_ICONS[categoryId] || '📊';
    
    const categoryGroup = el('div', { className: 'signal-picker__category' }, [
      el('div', { className: 'signal-picker__category-header' }, [
        el('span', { className: 'signal-picker__category-icon' }, [categoryIcon]),
        el('span', { className: 'signal-picker__category-name' }, [categoryName]),
      ]),
      el('div', { className: 'signal-picker__signals' },
        signalIds.map(signalId => {
          const isActive = activeSignals.includes(signalId);
          const isDisabled = !isActive && activeSignals.length >= MAX_SIGNALS;
          
          return el('button', {
            className: `signal-picker__signal ${isActive ? 'signal-picker__signal--active' : ''} ${isDisabled ? 'signal-picker__signal--disabled' : ''}`,
            'data-signal': signalId,
            disabled: isDisabled,
            onclick: () => {
              if (isActive) {
                removeSignalCallback?.(signalId);
              } else if (!isDisabled) {
                addSignalCallback?.(signalId);
              }
            },
          }, [
            el('span', { className: 'signal-picker__signal-name' }, [
              signalId.replace(/_/g, ' ')
            ]),
            isActive 
              ? el('span', { className: 'signal-picker__signal-check' }, ['✓'])
              : null,
          ].filter(Boolean));
        })
      ),
    ]);
    
    picker.appendChild(categoryGroup);
  });
  
  return picker;
}

function refreshSignalPicker() {
  if (!sidebarRef) return;
  
  const pickerContainer = sidebarRef.querySelector('.sidebar__picker-container');
  if (!pickerContainer) return;
  
  pickerContainer.innerHTML = '';
  pickerContainer.appendChild(buildSignalPicker());
  
  // Update count display
  const countEl = sidebarRef.querySelector('.sidebar__signal-count');
  if (countEl) {
    countEl.textContent = `${activeSignals.length}/${MAX_SIGNALS}`;
    countEl.classList.toggle('sidebar__signal-count--full', activeSignals.length >= MAX_SIGNALS);
  }
}

// ============================================================================
// Component
// ============================================================================

export function ChartModeSidebar(config = {}) {
  activeSignals = config.signals || [];
  addSignalCallback = config.onAddSignal;
  removeSignalCallback = config.onRemoveSignal;
  
  const sidebar = el('aside', { className: 'chart-mode__sidebar' }, [
    // Collapse toggle
    el('button', {
      className: 'sidebar__toggle',
      title: 'Toggle sidebar',
      onclick: () => {
        isCollapsed = !isCollapsed;
        sidebarRef?.classList.toggle('chart-mode__sidebar--collapsed', isCollapsed);
      },
    }, [
      el('span', { className: 'sidebar__toggle-icon' }, ['◀']),
    ]),
    
    // Sidebar content
    el('div', { className: 'sidebar__content' }, [
      // Header
      el('div', { className: 'sidebar__header' }, [
        el('h3', { className: 'sidebar__title' }, ['Signals']),
        el('span', { className: 'sidebar__signal-count' }, [`${activeSignals.length}/${MAX_SIGNALS}`]),
      ]),
      
      // Picker container
      el('div', { className: 'sidebar__picker-container' }, [
        buildSignalPicker(),
      ]),
      
      // Help text
      el('div', { className: 'sidebar__help' }, [
        el('p', {}, ['Click signals to add/remove from chart.']),
        el('p', {}, ['Maximum 4 signals for readability.']),
      ]),
    ]),
  ]);
  
  sidebarRef = sidebar;
  
  return sidebar;
}

/**
 * Update sidebar with new active signals
 */
export function updateSidebarSignals(signals) {
  activeSignals = signals;
  refreshSignalPicker();
}

/**
 * Collapse/expand sidebar
 */
export function toggleSidebar() {
  isCollapsed = !isCollapsed;
  sidebarRef?.classList.toggle('chart-mode__sidebar--collapsed', isCollapsed);
}

/**
 * Set sidebar collapsed state
 */
export function setSidebarCollapsed(collapsed) {
  isCollapsed = collapsed;
  sidebarRef?.classList.toggle('chart-mode__sidebar--collapsed', isCollapsed);
}
